import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Bus,
  Plus,
  Loader2,
  Trash2,
  Pencil,
  Search,
  Download,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const VEHICLE_DOCUMENTS = [
  { type: "rc", label: "RC Upload" },
  { type: "insurance", label: "Insurance Upload" },
  { type: "pollution", label: "Pollution Upload" },
  { type: "fitness", label: "Fitness Upload" },
  { type: "permit", label: "Permit Upload" },
];

const EMPTY_VEHICLE = {
  vehicle_name: "",
  vehicle_number: "",
  vehicle_type: "Bus",
  driver_name: "",
  driver_phone: "",
  capacity: 0,
  model_year: 0,
  pollution_validity: "",
  insurance_validity: "",
  fitness_validity: "",
  permit_validity: "",
  service_date: "",
  kilometers: 0,
  next_kilometers: 0,
  next_date: "",
  engine_number: "",
  chassis_number: "",
};

function sessionMonths(session) {
  const names = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  let start = new Date().getFullYear();
  if (session && /^\d{4}/.test(session)) start = parseInt(session.slice(0, 4), 10);
  return names.map((mm, i) => `${mm} ${i < 9 ? start : start + 1}`);
}

function VehicleDocumentActions({ vehicle, documentType, fileRecord }) {
  const [busy, setBusy] = useState(false);
  const [thumb, setThumb] = useState(null);
  const isImage = fileRecord && (fileRecord.content_type || "").startsWith("image/");

  useEffect(() => {
    let objectUrl;
    let active = true;
    if (isImage) {
      api
        .get(`/vehicles/${vehicle.id}/documents/${documentType}`, { responseType: "blob" })
        .then((response) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(response.data);
          setThumb(objectUrl);
        })
        .catch(() => {});
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileRecord?.id, isImage, vehicle.id, documentType]);

  const fetchDocument = async (shouldDownload) => {
    const viewer = shouldDownload ? null : window.open("", "_blank");
    setBusy(true);
    try {
      const response = await api.get(
        `/vehicles/${vehicle.id}/documents/${documentType}`,
        { responseType: "blob" }
      );
      const objectUrl = URL.createObjectURL(response.data);
      if (shouldDownload) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileRecord.original_filename;
        link.click();
      } else if (viewer) {
        viewer.location.href = objectUrl;
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      viewer?.close();
      toast.error(apiErr(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!fileRecord) {
    return (
      <span className="text-slate-300" data-testid={`vehicle-document-${vehicle.id}-${documentType}`}>
        —
      </span>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5"
      data-testid={`vehicle-document-${vehicle.id}-${documentType}`}
    >
      <button
        type="button"
        className="h-10 w-10 shrink-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center transition-all hover:ring-2 hover:ring-indigo-300 disabled:opacity-60"
        title={`View ${fileRecord.original_filename}`}
        aria-label={`View ${fileRecord.original_filename}`}
        disabled={busy}
        onClick={() => fetchDocument(false)}
        data-testid={`view-vehicle-document-${vehicle.id}-${documentType}`}
      >
        {busy ? (
          <Loader2 size={15} className="animate-spin text-indigo-500" />
        ) : thumb ? (
          <img src={thumb} alt={fileRecord.original_filename} className="h-full w-full object-cover" />
        ) : (
          <FileText size={18} className="text-rose-500" />
        )}
      </button>
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 text-emerald-600"
        title={`Download ${fileRecord.original_filename}`}
        aria-label={`Download ${fileRecord.original_filename}`}
        disabled={busy}
        onClick={() => fetchDocument(true)}
        data-testid={`download-vehicle-document-${vehicle.id}-${documentType}`}
      >
        <Download size={15} />
      </Button>
    </div>
  );
}

export default function Transport() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeOpen, setRouteOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [route, setRoute] = useState({ route_name: "", route_number: "", pickup_points: "", monthly_fee: 0, academic_session: "2026-27" });
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  const [vehicleFiles, setVehicleFiles] = useState({});
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  const [assign, setAssign] = useState({ student_id: "", route_id: "", pickup_point: "", vehicle_id: "", transport_fee: 0, transport_months: [] });
  const [students, setStudents] = useState([]);
  const [editRouteId, setEditRouteId] = useState(null);
  const [editVehicleId, setEditVehicleId] = useState(null);
  const [editAssignId, setEditAssignId] = useState(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [showStuList, setShowStuList] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignRouteFilter, setAssignRouteFilter] = useState("all");
  const [assignVehicleFilter, setAssignVehicleFilter] = useState("all");
  const [selectedAssigns, setSelectedAssigns] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [routeSearch, setRouteSearch] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/routes"), api.get("/vehicles"), api.get("/transport/assignments")])
      .then(([r, v, a]) => { setRoutes(r.data); setVehicles(v.data); setAssigns(a.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/students", { params: { limit: 1000 } }).then((r) => setStudents(r.data.students)).catch(() => {}); }, []);

  const canEdit = hasPerm(user, "edit_transport");
  const canDelete = hasPerm(user, "delete");

  const openRoute = (r) => {
    if (r) { setEditRouteId(r.id); setRoute({ route_name: r.route_name, route_number: r.route_number, pickup_points: (r.pickup_points || []).join(", "), monthly_fee: r.monthly_fee, academic_session: r.academic_session || "2026-27" }); }
    else { setEditRouteId(null); setRoute({ route_name: "", route_number: "", pickup_points: "", monthly_fee: 0, academic_session: "2026-27" }); }
    setRouteOpen(true);
  };
  const openVehicle = (v) => {
    setVehicleFiles({});
    setVehicleDocuments(v?.documents || []);
    if (v) {
      setEditVehicleId(v.id);
      setVehicle({ ...EMPTY_VEHICLE, ...v });
    } else {
      setEditVehicleId(null);
      setVehicle(EMPTY_VEHICLE);
    }
    setVehicleOpen(true);
  };
  const openAssign = (a) => {
    setStudentQuery(""); setShowStuList(false);
    if (a) { setEditAssignId(a.student_id); setAssign({ student_id: a.student_id, route_id: a.route_id || "", pickup_point: a.pickup_point || "", vehicle_id: a.vehicle_id || "", transport_fee: a.transport_fee || 0, transport_months: a.transport_months || [] }); }
    else { setEditAssignId(null); setAssign({ student_id: "", route_id: "", pickup_point: "", vehicle_id: "", transport_fee: 0, transport_months: [] }); }
    setAssignOpen(true);
  };

  const saveRoute = async () => {
    const payload = { ...route, monthly_fee: Number(route.monthly_fee), pickup_points: route.pickup_points.split(",").map((s) => s.trim()).filter(Boolean) };
    try { if (editRouteId) await api.put(`/routes/${editRouteId}`, payload); else await api.post("/routes", payload); toast.success(editRouteId ? "Route updated" : "Route added"); setRouteOpen(false); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveVehicle = async () => {
    const payload = {
      ...vehicle,
      capacity: Number(vehicle.capacity),
      model_year: Number(vehicle.model_year),
      kilometers: Number(vehicle.kilometers),
      next_kilometers: Number(vehicle.next_kilometers),
    };
    try {
      const savedVehicle = editVehicleId
        ? await api.put(`/vehicles/${editVehicleId}`, payload)
        : await api.post("/vehicles", payload);
      const uploads = Object.entries(vehicleFiles).filter(([, file]) => file);
      await Promise.all(uploads.map(([documentType, file]) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post(
          `/vehicles/${savedVehicle.data.id}/documents/${documentType}`,
          formData
        );
      }));
      toast.success(editVehicleId ? "Vehicle updated" : "Vehicle added");
      setVehicleOpen(false);
      load();
    } catch (error) {
      toast.error(apiErr(error.response?.data?.detail));
      load();
    }
  };
  const selectVehicleDocument = (documentType, file) => {
    if (!file) return;
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Upload a PDF, JPG, PNG, or WEBP document");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Documents must be 10 MB or smaller");
      return;
    }
    setVehicleFiles((current) => ({ ...current, [documentType]: file }));
  };
  const saveAssign = async () => {
    try { await api.post("/transport/assign", { ...assign, transport_fee: Number(assign.transport_fee) }); toast.success(editAssignId ? "Assignment updated" : "Assigned"); setAssignOpen(false); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delRoute = async (r) => {
    if (!window.confirm(`Delete route "${r.route_name}"?`)) return;
    try { await api.delete(`/routes/${r.id}`); toast.success("Route deleted"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delVehicle = async (v) => {
    if (!window.confirm(`Delete vehicle "${v.vehicle_number}"?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delAssign = async (a) => {
    if (!window.confirm(`Remove transport assignment for ${a.student_name}?`)) return;
    try { await api.delete(`/transport/assign/${a.student_id}`); toast.success("Assignment removed"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDelAssign = async () => {
    if (!selectedAssigns.length) return;
    if (!window.confirm(`Remove transport assignment for ${selectedAssigns.length} student(s)?`)) return;
    try {
      await Promise.all(selectedAssigns.map((sid) => api.delete(`/transport/assign/${sid}`)));
      toast.success("Assignments removed"); setSelectedAssigns([]); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const monthsCount = (a) => ((a.transport_months || []).length || 12);
  const assignTotal = (a) => Number(a.transport_fee || 0) * monthsCount(a);
  const isActive = (a) => a.student_active !== false;

  const selStudent = students.find((s) => s.id === assign.student_id);
  const filteredAssigns = assigns.filter((a) => {
    if (assignRouteFilter !== "all" && (a.route_id || "") !== assignRouteFilter) return false;
    if (assignVehicleFilter !== "all" && (a.vehicle_id || "") !== assignVehicleFilter) return false;
    const q = assignSearch.trim().toLowerCase();
    if (!q) return true;
    return (a.student_name || "").toLowerCase().includes(q) || (a.admission_number || "").toLowerCase().includes(q) || (a.father_name || "").toLowerCase().includes(q) || (a.vehicle_number || "").toLowerCase().includes(q);
  });
  const filteredRoutes = routes.filter((r) => {
    const query = routeSearch.trim().toLowerCase();
    if (!query) return true;
    return [r.route_name, String(r.route_number || ""), (r.pickup_points || []).join(", ")]
      .some((value) => (value || "").toLowerCase().includes(query));
  });
  const filteredVehicles = vehicles.filter((v) => {
    const query = vehicleSearch.trim().toLowerCase();
    if (!query) return true;
    return [v.vehicle_name, v.vehicle_number, v.driver_name]
      .some((value) => (value || "").toLowerCase().includes(query));
  });
  const allSelected = filteredAssigns.length > 0 && filteredAssigns.every((a) => selectedAssigns.includes(a.student_id));
  const toggleSel = (sid) => setSelectedAssigns((cur) => cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid]);
  const toggleSelAll = () => setSelectedAssigns(allSelected ? [] : filteredAssigns.map((a) => a.student_id));
  const filteredStudents = students.filter((s) => {
    const q = studentQuery.trim().toLowerCase();
    if (!q) return true;
    return (s.name || "").toLowerCase().includes(q) || (s.admission_number || "").toLowerCase().includes(q) || (s.father_name || "").toLowerCase().includes(q);
  }).slice(0, 8);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900">Transport</h1>
      <Tabs defaultValue="routes">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList><TabsTrigger value="routes" data-testid="tab-routes">Routes</TabsTrigger><TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger><TabsTrigger value="assign" data-testid="tab-assign">Assignments</TabsTrigger></TabsList>
        </div>

        <TabsContent value="routes" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {canEdit && <Button onClick={() => openRoute(null)} data-testid="add-route-btn"><Plus size={18} className="mr-2" />Add Route</Button>}
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search route name, number or stop" value={routeSearch} onChange={(e) => setRouteSearch(e.target.value)} data-testid="route-search" />
            </div>
            <span className="text-xs text-slate-400 ml-auto">{filteredRoutes.length} of {routes.length}</span>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                <th className="text-left px-3 py-3 w-12">Se. No.</th>
                <th className="text-left px-4 py-3">Route #</th>
                <th className="text-left px-4 py-3">Route Name</th>
                <th className="text-left px-4 py-3">Stops</th>
                <th className="text-center px-4 py-3">Students</th>
                <th className="text-right px-4 py-3">Monthly Fee</th>
                {canEdit && <th className="text-right px-4 py-3">Actions</th>}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRoutes.map((r, i) => (
                  <tr key={r.id} data-testid={`route-${r.route_name}`}>
                    <td className="px-3 py-3 text-slate-500 tabular">{i + 1}</td>
                    <td className="px-4 py-3 text-slate-600">{r.route_number}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2 font-medium text-slate-800"><Bus size={16} className="text-indigo-600" />{r.route_name}</div></td>
                    <td className="px-4 py-3 text-slate-600">{r.pickup_points.join(", ")}</td>
                    <td className="px-4 py-3 text-center tabular font-semibold text-slate-700" data-testid={`route-students-${r.route_name}`}>{assigns.filter((a) => (a.route_id || "") === r.id).length}</td>
                    <td className="px-4 py-3 text-right font-semibold text-indigo-700 tabular">{fmt(r.monthly_fee)}/mo</td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => openRoute(r)} data-testid={`edit-route-${r.route_name}`}><Pencil size={14} /></Button>
                        {canDelete && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => delRoute(r)} data-testid={`delete-route-${r.route_name}`}><Trash2 size={14} /></Button>}
                      </td>
                    )}
                  </tr>
                ))}
                {filteredRoutes.length === 0 && <tr><td colSpan={canEdit ? 7 : 6} className="text-center py-8 text-slate-400">No routes.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="vehicles" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {canEdit && (
              <Button onClick={() => openVehicle(null)} data-testid="add-vehicle-btn">
                <Plus size={18} className="mr-2" />
                Add Vehicle
              </Button>
            )}
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                className="pl-9"
                placeholder="Search vehicle name, number or driver"
                value={vehicleSearch}
                onChange={(event) => setVehicleSearch(event.target.value)}
                data-testid="vehicle-search"
              />
            </div>
            {vehicleSearch && (
              <Button
                variant="ghost"
                className="text-slate-500"
                onClick={() => setVehicleSearch("")}
                data-testid="clear-vehicle-search"
              >
                Clear
              </Button>
            )}
            <span className="text-xs text-slate-400 ml-auto" data-testid="vehicle-search-count">
              {filteredVehicles.length} of {vehicles.length}
            </span>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-3 w-12">Se. No.</th>
                  <th className="text-left px-4 py-3">Vehicle Name</th>
                  <th className="text-left px-4 py-3">Vehicle Number</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Driver</th>
                  <th className="text-left px-4 py-3">Driver Phone</th>
                  <th className="text-right px-4 py-3">Capacity</th>
                  <th className="text-right px-4 py-3">Model Year</th>
                  <th className="text-left px-4 py-3">Pollution</th>
                  <th className="text-left px-4 py-3">Insurance</th>
                  <th className="text-left px-4 py-3">Fitness</th>
                  <th className="text-left px-4 py-3">Permit</th>
                  <th className="text-left px-4 py-3">Service Date</th>
                  <th className="text-right px-4 py-3">Kilometers</th>
                  <th className="text-right px-4 py-3">Next Kilometers</th>
                  <th className="text-left px-4 py-3">Next Date</th>
                  <th className="text-left px-4 py-3">Engine Number</th>
                  <th className="text-left px-4 py-3">Chassis No.</th>
                  {VEHICLE_DOCUMENTS.map((document) => (
                    <th key={document.type} className="text-left px-4 py-3">
                      {document.label}
                    </th>
                  ))}
                  {canEdit && <th className="text-right px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredVehicles.map((v, i) => (
                  <tr key={v.id} data-testid={`vehicle-${v.vehicle_number}`}>
                    <td className="px-3 py-3 text-slate-500 tabular">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{v.vehicle_name || "—"}</td>
                    <td className="px-4 py-3">{v.vehicle_number}</td>
                    <td className="px-4 py-3 text-slate-600">{v.vehicle_type || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.driver_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{v.driver_phone || "—"}</td>
                    <td className="px-4 py-3 text-right tabular">{v.capacity || 0}</td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular">
                      {v.model_year || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular">{fmtDate(v.pollution_validity)}</td>
                    <td className="px-4 py-3 text-slate-600 tabular">{fmtDate(v.insurance_validity)}</td>
                    <td className="px-4 py-3 text-slate-600 tabular">{fmtDate(v.fitness_validity)}</td>
                    <td className="px-4 py-3 text-slate-600 tabular">{fmtDate(v.permit_validity)}</td>
                    <td className="px-4 py-3 text-slate-600 tabular">
                      {fmtDate(v.service_date)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular">
                      {v.kilometers || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular">
                      {v.next_kilometers || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular">
                      {fmtDate(v.next_date)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular">
                      {v.engine_number || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular">
                      {v.chassis_number || "—"}
                    </td>
                    {VEHICLE_DOCUMENTS.map((document) => (
                      <td key={document.type} className="px-4 py-3">
                        <VehicleDocumentActions
                          vehicle={v}
                          documentType={document.type}
                          fileRecord={(v.documents || []).find(
                            (file) => file.document_type === document.type
                          )}
                        />
                      </td>
                    ))}
                    {canEdit && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => openVehicle(v)} data-testid={`edit-vehicle-${v.vehicle_number}`}><Pencil size={14} /></Button>
                        {canDelete && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => delVehicle(v)} data-testid={`delete-vehicle-${v.vehicle_number}`}><Trash2 size={14} /></Button>}
                      </td>
                    )}
                  </tr>
                ))}
                {filteredVehicles.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 24 : 23} className="text-center py-8 text-slate-400">
                      {vehicles.length === 0 ? "No vehicles yet." : "No matching vehicles."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="assign" className="mt-4 space-y-4">
          {canDelete && selectedAssigns.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="text-rose-600 border-rose-200" onClick={bulkDelAssign} data-testid="bulk-delete-assign-btn"><Trash2 size={16} className="mr-1" />Delete Selected ({selectedAssigns.length})</Button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && <Button onClick={() => openAssign(null)} data-testid="add-assign-btn"><Plus size={18} className="mr-2" />Assign Student</Button>}
            <Input placeholder="Search student, father, adm no. or vehicle" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} className="max-w-xs" data-testid="assign-search" />
            <Select value={assignRouteFilter} onValueChange={setAssignRouteFilter}>
              <SelectTrigger className="w-56" data-testid="assign-route-filter"><SelectValue placeholder="All Routes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Routes</SelectItem>
                {routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={assignVehicleFilter} onValueChange={setAssignVehicleFilter}>
              <SelectTrigger className="w-56" data-testid="assign-vehicle-filter"><SelectValue placeholder="All Vehicles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.vehicle_name || v.vehicle_number}</SelectItem>)}
              </SelectContent>
            </Select>
            {(assignSearch || assignRouteFilter !== "all" || assignVehicleFilter !== "all") && (
              <Button variant="ghost" className="text-slate-500" onClick={() => { setAssignSearch(""); setAssignRouteFilter("all"); setAssignVehicleFilter("all"); }} data-testid="assign-clear-filter">Clear</Button>
            )}
            <span className="text-xs text-slate-400 ml-auto">{filteredAssigns.length} of {assigns.length}</span>
            <div className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2" data-testid="assign-grand-total" title="Active students only (matches View Fees)">
              <span className="text-xs font-medium text-slate-500">Transport (active)</span>
              <span className="text-lg font-bold text-indigo-700 tabular">{fmt(filteredAssigns.filter(isActive).reduce((sum, a) => sum + assignTotal(a), 0))}</span>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={allSelected} onChange={toggleSelAll} data-testid="assign-select-all" /></th>
                <th className="text-left px-3 py-3 w-12">Se. No.</th>
                <th className="text-left px-4 py-3">Student</th>
                <th className="text-left px-4 py-3">Father Name</th>
                <th className="text-left px-4 py-3">Route</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Pickup</th>
                <th className="text-right px-4 py-3">Fee</th>
                <th className="text-center px-4 py-3">Month(s)</th>
                <th className="text-right px-4 py-3">Total</th>
                {canEdit && <th className="text-right px-4 py-3">Actions</th>}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAssigns.map((a, i) => (<tr key={a.student_id} data-testid={`assign-row-${a.admission_number}`} className={`${selectedAssigns.includes(a.student_id) ? "bg-indigo-50/40" : ""} ${isActive(a) ? "" : "opacity-50"}`}>
                  <td className="px-3 py-3"><input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={selectedAssigns.includes(a.student_id)} onChange={() => toggleSel(a.student_id)} data-testid={`assign-select-${a.admission_number}`} /></td>
                  <td className="px-3 py-3 text-slate-500 tabular">{i + 1}</td>
                  <td className="px-4 py-3 font-medium">{a.student_name}{!isActive(a) && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 align-middle">Inactive</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{a.father_name || "—"}</td>
                  <td className="px-4 py-3">{a.route}</td>
                  <td className="px-4 py-3">{a.vehicle_number || "—"}</td>
                  <td className="px-4 py-3">{a.pickup_point}</td>
                  <td className="px-4 py-3 text-right tabular">{fmt(a.transport_fee)}/mo</td>
                  <td className="px-4 py-3 text-center tabular" title={(a.transport_months || []).join(", ") || "All 12 months"} data-testid={`assign-months-${a.admission_number}`}>{monthsCount(a)}{(a.transport_months || []).length ? "" : " (all)"}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold text-indigo-700" data-testid={`assign-total-${a.admission_number}`}>{fmt(assignTotal(a))}</td>
                  {canEdit && <td className="px-4 py-3 text-right whitespace-nowrap"><Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => openAssign(a)} data-testid={`edit-assign-${a.admission_number}`}><Pencil size={14} /></Button><Button size="sm" variant="ghost" className="text-rose-600" onClick={() => delAssign(a)} data-testid={`delete-assign-${a.admission_number}`}><Trash2 size={14} /></Button></td>}
                </tr>))}
                {filteredAssigns.length === 0 && <tr><td colSpan={canEdit ? 11 : 10} className="text-center py-8 text-slate-400">No assignments.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={routeOpen} onOpenChange={setRouteOpen}><DialogContent><DialogHeader><DialogTitle>{editRouteId ? "Edit Route" : "Add Route"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Route Name</Label><Input value={route.route_name} onChange={(e) => setRoute({ ...route, route_name: e.target.value })} data-testid="route-name" /></div>
          <div><Label>Route Number</Label><Input value={route.route_number} onChange={(e) => setRoute({ ...route, route_number: e.target.value })} /></div>
          <div><Label>Pickup Points (comma separated)</Label><Input value={route.pickup_points} onChange={(e) => setRoute({ ...route, pickup_points: e.target.value })} data-testid="route-points" /></div>
          <div><Label>Monthly Fee</Label><Input type="number" value={route.monthly_fee} onChange={(e) => setRoute({ ...route, monthly_fee: e.target.value })} data-testid="route-fee" /></div>
        </div><DialogFooter><Button onClick={saveRoute} data-testid="save-route-btn">Save</Button></DialogFooter></DialogContent></Dialog>

      <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-y-auto"
          data-testid="vehicle-form-dialog"
        >
          <DialogHeader>
            <DialogTitle>{editVehicleId ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label>Vehicle Name</Label>
              <Input
                value={vehicle.vehicle_name}
                onChange={(event) => setVehicle({ ...vehicle, vehicle_name: event.target.value })}
                placeholder="e.g. School Bus 1"
                data-testid="vehicle-name"
              />
            </div>
            <div>
              <Label>Vehicle Number</Label>
              <Input
                value={vehicle.vehicle_number}
                onChange={(event) => setVehicle({ ...vehicle, vehicle_number: event.target.value })}
                data-testid="vehicle-number"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Input
                value={vehicle.vehicle_type}
                onChange={(event) => setVehicle({ ...vehicle, vehicle_type: event.target.value })}
                data-testid="vehicle-type"
              />
            </div>
            <div>
              <Label>Driver Name</Label>
              <Input
                value={vehicle.driver_name}
                onChange={(event) => setVehicle({ ...vehicle, driver_name: event.target.value })}
                data-testid="vehicle-driver-name"
              />
            </div>
            <div>
              <Label>Driver Phone</Label>
              <Input
                value={vehicle.driver_phone}
                onChange={(event) => setVehicle({ ...vehicle, driver_phone: event.target.value })}
                data-testid="vehicle-driver-phone"
              />
            </div>
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                min="0"
                value={vehicle.capacity}
                onChange={(event) => setVehicle({ ...vehicle, capacity: event.target.value })}
                data-testid="vehicle-capacity"
              />
            </div>
            <div>
              <Label>Model Year</Label>
              <Input
                type="number"
                min="1900"
                max="2100"
                value={vehicle.model_year || ""}
                onChange={(event) => setVehicle({ ...vehicle, model_year: event.target.value })}
                data-testid="vehicle-model-year"
              />
            </div>
            <div>
              <Label>Service Date</Label>
              <Input
                type="date"
                value={vehicle.service_date}
                onChange={(event) => setVehicle({ ...vehicle, service_date: event.target.value })}
                data-testid="vehicle-service-date"
              />
            </div>
            <div>
              <Label>Kilometers</Label>
              <Input
                type="number"
                min="0"
                value={vehicle.kilometers}
                onChange={(event) => setVehicle({ ...vehicle, kilometers: event.target.value })}
                data-testid="vehicle-kilometers"
              />
            </div>
            <div>
              <Label>Next Kilometers</Label>
              <Input
                type="number"
                min="0"
                value={vehicle.next_kilometers}
                onChange={(event) => setVehicle({ ...vehicle, next_kilometers: event.target.value })}
                data-testid="vehicle-next-kilometers"
              />
            </div>
            <div>
              <Label>Next Date</Label>
              <Input
                type="date"
                value={vehicle.next_date}
                onChange={(event) => setVehicle({ ...vehicle, next_date: event.target.value })}
                data-testid="vehicle-next-date"
              />
            </div>
            <div>
              <Label>Engine Number</Label>
              <Input
                value={vehicle.engine_number}
                onChange={(event) => setVehicle({ ...vehicle, engine_number: event.target.value })}
                data-testid="vehicle-engine-number"
              />
            </div>
            <div>
              <Label>Chassis No.</Label>
              <Input
                value={vehicle.chassis_number}
                onChange={(event) => setVehicle({ ...vehicle, chassis_number: event.target.value })}
                data-testid="vehicle-chassis-number"
              />
            </div>
            <div>
              <Label>Pollution Validity</Label>
              <Input
                type="date"
                value={vehicle.pollution_validity}
                onChange={(event) => setVehicle({ ...vehicle, pollution_validity: event.target.value })}
                data-testid="vehicle-pollution"
              />
            </div>
            <div>
              <Label>Insurance Validity</Label>
              <Input
                type="date"
                value={vehicle.insurance_validity}
                onChange={(event) => setVehicle({ ...vehicle, insurance_validity: event.target.value })}
                data-testid="vehicle-insurance"
              />
            </div>
            <div>
              <Label>Fitness Validity</Label>
              <Input
                type="date"
                value={vehicle.fitness_validity}
                onChange={(event) => setVehicle({ ...vehicle, fitness_validity: event.target.value })}
                data-testid="vehicle-fitness"
              />
            </div>
            <div>
              <Label>Permit Validity</Label>
              <Input
                type="date"
                value={vehicle.permit_validity}
                onChange={(event) => setVehicle({ ...vehicle, permit_validity: event.target.value })}
                data-testid="vehicle-permit"
              />
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-heading font-bold text-slate-800 mb-3">Vehicle Documents</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {VEHICLE_DOCUMENTS.map((document) => {
                const existing = vehicleDocuments.find(
                  (file) => file.document_type === document.type
                );
                const selected = vehicleFiles[document.type];
                return (
                  <div key={document.type}>
                    <Label>{document.label}</Label>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(event) => selectVehicleDocument(
                        document.type,
                        event.target.files?.[0]
                      )}
                      data-testid={`vehicle-${document.type}-upload`}
                    />
                    <p
                      className="mt-1 text-xs text-slate-400 truncate"
                      data-testid={`vehicle-${document.type}-upload-status`}
                    >
                      {selected?.name || existing?.original_filename || "PDF, JPG, PNG, or WEBP · max 10 MB"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveVehicle} data-testid="save-vehicle-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}><DialogContent className="max-w-2xl max-h-[95vh] flex flex-col p-0 gap-0"><DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>{editAssignId ? "Edit Assignment" : "Assign Transport"}</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-y-auto px-6 flex-1 pb-1">
          <div><Label>Student</Label>
            {editAssignId ? (
              <Input value={selStudent ? `${selStudent.name} (${selStudent.admission_number})` : assign.student_id} disabled data-testid="assign-student-locked" />
            ) : (
              <div className="relative">
                <Input placeholder="Search by student or father name" value={studentQuery} onFocus={() => setShowStuList(true)} onChange={(e) => { setStudentQuery(e.target.value); setShowStuList(true); if (assign.student_id) setAssign({ ...assign, student_id: "" }); }} data-testid="assign-student-search" />
                {showStuList && studentQuery && (
                  <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                    {filteredStudents.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No students found</div>}
                    {filteredStudents.map((s) => (
                      <button key={s.id} type="button" onClick={() => { setAssign({ ...assign, student_id: s.id }); setStudentQuery(`${s.name} (${s.admission_number})`); setShowStuList(false); }} className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm" data-testid={`assign-option-${s.admission_number}`}>
                        <div className="font-medium text-slate-800">{s.name} <span className="text-xs text-slate-400">({s.admission_number})</span></div>
                        <div className="text-xs text-slate-400">Father: {s.father_name || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {selStudent && <div className="text-xs text-slate-500 mt-1" data-testid="assign-selected-father">Father: {selStudent.father_name || "—"}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Route</Label><Select value={assign.route_id} onValueChange={(v) => { const r = routes.find((x) => x.id === v); setAssign({ ...assign, route_id: v, transport_fee: r?.monthly_fee || 0, pickup_point: (r?.pickup_points || []).includes(assign.pickup_point) ? assign.pickup_point : "" }); }}><SelectTrigger data-testid="assign-route"><SelectValue placeholder="Select route" /></SelectTrigger><SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Vehicle</Label><Select value={assign.vehicle_id} onValueChange={(v) => setAssign({ ...assign, vehicle_id: v })}><SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger><SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.vehicle_name || v.vehicle_number}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Pickup Point</Label>
              {(() => { const pk = (routes.find((r) => r.id === assign.route_id)?.pickup_points) || []; return (
                <Select value={assign.pickup_point} onValueChange={(v) => setAssign({ ...assign, pickup_point: v })} disabled={!assign.route_id || pk.length === 0}>
                  <SelectTrigger data-testid="assign-pickup"><SelectValue placeholder={!assign.route_id ? "Select a route first" : (pk.length ? "Select pickup point" : "No pickup points for this route")} /></SelectTrigger>
                  <SelectContent>{pk.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              ); })()}
            </div>
            <div><Label>Transport Fee (monthly)</Label><Input type="number" value={assign.transport_fee} onChange={(e) => setAssign({ ...assign, transport_fee: e.target.value })} data-testid="assign-fee" /></div>
          </div>
          <div>
            <Label>Transport Months <span className="text-xs text-slate-400">({(assign.transport_months || []).length ? `${assign.transport_months.length} selected` : "all 12"})</span></Label>
            <label className="flex items-center gap-2 mt-1 mb-1.5 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={(assign.transport_months || []).length === sessionMonths().length} onChange={(e) => setAssign({ ...assign, transport_months: e.target.checked ? sessionMonths() : [] })} data-testid="assign-select-all-months" />
              Select All Months
            </label>
            <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid="transport-month-list">
              {sessionMonths().map((m) => {
                const on = (assign.transport_months || []).includes(m);
                return (
                  <button key={m} type="button" onClick={() => { const cur = assign.transport_months || []; const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]; setAssign({ ...assign, transport_months: next }); }} className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`} data-testid={`tmonth-${m.replace(" ", "-")}`}>{m}</button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-1">Leave all unselected to charge transport for all 12 months. Fee = monthly × months selected.</p>
          </div>
        </div><DialogFooter className="px-6 py-4 border-t shrink-0"><Button onClick={saveAssign} data-testid="save-assign-btn">Save Transport</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}
